import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import type { EntityDependencies } from '../../types/rbac';

interface DependencyDeleteDialogProps {
  /** The record being deleted; null closes the dialog. */
  entityId: string | null;
  /** Human label used in copy, e.g. "permission", "role binding". */
  entityLabel: string;
  /** Cache discriminator so each resource caches its own lookups. */
  resourceKey: string;
  /** Fetches "what references this record" from the backend. */
  getDependencies: (id: string) => Promise<EntityDependencies>;
  onCancel: () => void;
  onConfirm: () => void;
  deleting?: boolean;
}

// A single "used by" group with a readable chip list.
const DependencyGroup = ({ title, items }: { title: string; items: string[] }) => {
  if (!items.length) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
          {title}
        </span>
        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
          {items.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((label, i) => (
          <span
            key={`${label}-${i}`}
            className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Delete dialog that first checks server-side dependencies.
 *  - While something still references the record, deletion is BLOCKED and the
 *    dependents are listed so the admin knows exactly what to detach first.
 *  - When nothing references it, it behaves like a normal delete confirmation.
 * This mirrors the backend, which refuses the same deletes with a 409.
 */
const DependencyDeleteDialog = ({
  entityId,
  entityLabel,
  resourceKey,
  getDependencies,
  onCancel,
  onConfirm,
  deleting = false,
}: DependencyDeleteDialogProps) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['rbac', 'dependencies', resourceKey, entityId],
    queryFn: () => getDependencies(entityId as string),
    enabled: !!entityId,
    staleTime: 0,
  });

  const d = data?.dependents;
  const blocked = !!data && !data.canDelete;

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        label={blocked ? 'Close' : 'Cancel'}
        severity="secondary"
        outlined
        size="small"
        onClick={onCancel}
        disabled={deleting}
      />
      {!blocked && (
        <Button
          label={deleting ? 'Deleting...' : 'Delete'}
          severity="danger"
          size="small"
          onClick={onConfirm}
          disabled={deleting || isLoading || isError}
          icon={deleting ? 'pi pi-spin pi-spinner' : undefined}
        />
      )}
    </div>
  );

  return (
    <Dialog
      visible={!!entityId}
      onHide={onCancel}
      header={blocked ? `Cannot delete this ${entityLabel}` : 'Confirm Delete'}
      footer={footer}
      style={{ width: '480px', maxWidth: '95vw' }}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-ink-muted">
          <i className="pi pi-spin pi-spinner" />
          Checking dependencies...
        </div>
      ) : isError ? (
        <div className="flex items-start gap-3">
          <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
          <p className="m-0 text-sm leading-relaxed text-ink-muted">
            Could not verify dependencies. Please try again.
          </p>
        </div>
      ) : blocked && d ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <i className="pi pi-ban flex-shrink-0 text-2xl text-rose-500" />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              This {entityLabel} is still used by the records below. Remove these references first,
              then delete it.
            </p>
          </div>
          <DependencyGroup title="Permission Sets" items={d.permissionSets.map((x) => x.key || x.displayName || x._id)} />
          <DependencyGroup title="Roles" items={d.roles.map((x) => x.key || x.displayName || x._id)} />
          <DependencyGroup
            title="Role Bindings"
            items={d.roleBindings.map((x) => `${x.role ?? '?'} @ ${x.unit ?? 'Global'}`)}
          />
          <DependencyGroup title="User Assignments" items={d.assignments.map((x) => x.user || x._id)} />
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
          <p className="m-0 text-sm leading-relaxed text-ink-muted">
            Nothing depends on this {entityLabel}. Are you sure you want to delete it? This action
            cannot be undone.
          </p>
        </div>
      )}
    </Dialog>
  );
};

export default DependencyDeleteDialog;
